import { create } from 'zustand';
import type { SuccessionSlateFact } from '../models/SuccessionPlanningPersistence';
import { orgPlanningEditingContextKey } from './orgPlanningScenarioStore';

export interface OptimisticSuccessionSlate {
  readonly revision: number;
  readonly slate: SuccessionSlateFact;
  readonly phase: 'saving' | 'accepted' | 'unsaved';
  readonly error?: string;
}

type SlatesByTargetRef = Readonly<Record<string, OptimisticSuccessionSlate>>;

interface SuccessionOptimisticSlateState {
  readonly slatesByContext: Readonly<Record<string, SlatesByTargetRef>>;
  readonly nextRevision: number;
  stage: (context: string, slate: SuccessionSlateFact) => number;
  accept: (context: string, targetRef: string, revision: number) => void;
  markUnsaved: (context: string, targetRef: string, revision: number, error: string) => void;
  restore: (
    context: string,
    targetRef: string,
    failedRevision: number,
    slate: SuccessionSlateFact,
  ) => void;
  settle: (context: string, isSettled: (slate: SuccessionSlateFact, targetRef: string) => boolean) => void;
  clearTarget: (context: string, targetRef: string) => void;
  clear: (context: string) => void;
  clearAll: () => void;
  peek: (context: string) => SlatesByTargetRef;
}

const EMPTY: SlatesByTargetRef = Object.freeze({});

/**
 * Optimistic succession slates, shared per diagram-owned editing context.
 *
 * The property panel stages an edit before the change-set round trip so the
 * inputs stay live. The canvas renders succession connectors from the same
 * projection, so this cannot be component state: keeping it in each hook
 * instance left the canvas blind to the staged edit and its connector did not
 * appear until a later refetch happened to land.
 */
export const useSuccessionOptimisticSlateStore = create<SuccessionOptimisticSlateState>((set, get) => ({
  slatesByContext: {},
  nextRevision: 0,

  stage: (context, slate) => {
    const revision = get().nextRevision + 1;
    set((state) => ({
      nextRevision: revision,
      slatesByContext: {
        ...state.slatesByContext,
        [context]: {
          ...(state.slatesByContext[context] ?? {}),
          [slate.targetRef]: { revision, slate, phase: 'saving' },
        },
      },
    }));
    return revision;
  },

  accept: (context, targetRef, revision) => {
    const current = get().slatesByContext[context]?.[targetRef];
    if (!current || current.revision !== revision) return;
    set((state) => ({
      slatesByContext: {
        ...state.slatesByContext,
        [context]: {
          ...(state.slatesByContext[context] ?? {}),
          [targetRef]: { ...current, phase: 'accepted', error: undefined },
        },
      },
    }));
  },

  markUnsaved: (context, targetRef, revision, error) => {
    const current = get().slatesByContext[context]?.[targetRef];
    if (!current || current.revision !== revision) return;
    set((state) => ({
      slatesByContext: {
        ...state.slatesByContext,
        [context]: {
          ...(state.slatesByContext[context] ?? {}),
          [targetRef]: { ...current, phase: 'unsaved', error },
        },
      },
    }));
  },

  // A rollback only applies while the failed revision is still the newest one;
  // a later edit has already superseded what the failure would restore.
  restore: (context, targetRef, failedRevision, slate) => {
    if (get().slatesByContext[context]?.[targetRef]?.revision !== failedRevision) return;
    const revision = get().nextRevision + 1;
    set((state) => ({
      nextRevision: revision,
      slatesByContext: {
        ...state.slatesByContext,
        [context]: {
          ...(state.slatesByContext[context] ?? {}),
          [targetRef]: { revision, slate, phase: 'unsaved' },
        },
      },
    }));
  },

  settle: (context, isSettled) => {
    const current = get().slatesByContext[context];
    if (!current) return;
    const remaining: Record<string, OptimisticSuccessionSlate> = {};
    let changed = false;
    for (const [targetRef, optimistic] of Object.entries(current)) {
      // Equality with a cached projection is not a save receipt. In-flight or
      // rejected input must survive even when it happens to match old data.
      if (optimistic.phase === 'accepted' && isSettled(optimistic.slate, targetRef)) changed = true;
      else remaining[targetRef] = optimistic;
    }
    if (!changed) return;
    set((state) => ({ slatesByContext: { ...state.slatesByContext, [context]: remaining } }));
  },

  clearTarget: (context, targetRef) => {
    const current = get().slatesByContext[context];
    if (!current?.[targetRef]) return;
    const remaining = { ...current };
    delete remaining[targetRef];
    set((state) => ({ slatesByContext: { ...state.slatesByContext, [context]: remaining } }));
  },

  clear: (context) => {
    if (!get().slatesByContext[context]) return;
    set((state) => ({ slatesByContext: { ...state.slatesByContext, [context]: EMPTY } }));
  },

  clearAll: () => set({ slatesByContext: {}, nextRevision: 0 }),

  peek: (context) => get().slatesByContext[context] ?? EMPTY,
}));

export function successionOptimisticContext(folderId: string, diagramId: string): string {
  return `${folderId}:${diagramId}`;
}

/** Protected values are scoped to a plan; the command lane remains diagram-wide. */
export function successionOptimisticDraftContext(folderId: string, diagramId: string): string {
  return `${successionOptimisticContext(folderId, diagramId)}|${encodeURIComponent(orgPlanningEditingContextKey(diagramId))}`;
}
